import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useKeycloak } from '@react-keycloak/web';
import ApiConfig from '../../../utils/services/apiConfig';
import { useLatestRef, useMountEffect } from '../../../utils/hooks';

const INTEGRATION_TYPES = ['webhook', 'mqtt', 'telegram', 'email', 'internal_service', 'google', 'custom'];
const AUTH_METHODS = ['none', 'api_key', 'bearer_token', 'basic', 'custom'];
const ACTION_TYPES = ['post_json', 'publish', 'send_message', 'send_email', 'create_alert', 'append_row', 'custom'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const emptyJson = '{}';

const readResponse = (response, fallback) => {
  if (!response) return fallback;
  if (Array.isArray(response)) return response;
  if (response.RobotResponse !== undefined) return response.RobotResponse;
  if (response.results?.RobotResponse !== undefined) return response.results.RobotResponse;
  return fallback;
};

const stringifyJson = (value) => JSON.stringify(value || {}, null, 2);

const parseJson = (value, fieldName) => {
  try {
    const parsed = JSON.parse(value || emptyJson);
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
      throw new Error(`${fieldName} must be a JSON object.`);
    }
    return parsed;
  } catch (error) {
    throw new Error(`${fieldName}: ${error.message}`);
  }
};

const userNameFrom = (keycloak) => keycloak.tokenParsed?.preferred_username || 'system';

const connectionDefaults = (userName) => ({
  Name: '',
  IntegrationType: 'webhook',
  BaseUrl: '',
  Host: '',
  Port: '',
  AuthMethod: 'none',
  Username: '',
  Password: '',
  ApiKey: '',
  Token: '',
  HeaderName: '',
  HeaderPrefix: '',
  DefaultHeaders: emptyJson,
  Config: emptyJson,
  Active: true,
  CreatedBy: userName,
  UpdatedBy: userName,
});

const actionDefaults = (connectionId, userName) => ({
  IntegrationConnectionId: connectionId,
  Name: '',
  ActionType: 'post_json',
  Method: 'POST',
  Path: '',
  Topic: '',
  Recipient: '',
  Headers: emptyJson,
  QueryParams: emptyJson,
  BodyTemplate: emptyJson,
  InputSchema: emptyJson,
  OutputSchema: emptyJson,
  TimeoutSeconds: 30,
  RetryCount: 0,
  ContinueOnError: false,
  Active: true,
  CreatedBy: userName,
  UpdatedBy: userName,
});

function IntegrationManagement() {
  const { keycloak } = useKeycloak();
  const apiConfig = useMemo(() => new ApiConfig(keycloak).api(), [keycloak]);
  const userName = userNameFrom(keycloak);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [connections, setConnections] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [connectionDialog, setConnectionDialog] = useState(null);
  const [actionDialog, setActionDialog] = useState(null);

  const selectedConnection = connections.find((connection) => connection.Id === selectedConnectionId) || null;

  const filteredConnections = connections.filter((connection) =>
    Object.values(connection).some((value) =>
      String(value ?? '').toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const loadConnections = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiConfig.fetchAllIntegrationConnections();
      const nextConnections = readResponse(response, []);
      setConnections(nextConnections);
      if (!selectedConnectionId && nextConnections.length > 0) {
        setSelectedConnectionId(nextConnections[0].Id);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async (connectionId) => {
    if (!connectionId) {
      setActions([]);
      return;
    }
    setError('');
    try {
      const response = await apiConfig.fetchIntegrationActionsByConnectionId(connectionId);
      setActions(readResponse(response, []));
    } catch (err) {
      setError(String(err));
    }
  };

  const loadActionsRef = useLatestRef(loadActions);

  useMountEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    loadActionsRef.current(selectedConnectionId);
  }, [selectedConnectionId, loadActionsRef]);

  const refreshSelected = async () => {
    await loadConnections();
    if (selectedConnectionId) await loadActions(selectedConnectionId);
  };

  const saveConnection = async (form) => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        Port: form.Port === '' ? null : Number(form.Port),
        DefaultHeaders: parseJson(form.DefaultHeaders, 'Default headers'),
        Config: parseJson(form.Config, 'Config'),
        UpdatedBy: userName,
      };
      ['Password', 'ApiKey', 'Token'].forEach((secretField) => {
        if (!body[secretField]) delete body[secretField];
      });
      let savedConnection = null;
      if (form.Id) {
        const response = await apiConfig.updateIntegrationConnection(form.Id, body);
        savedConnection = readResponse(response, null);
      } else {
        const response = await apiConfig.createIntegrationConnection({ ...body, CreatedBy: userName });
        savedConnection = readResponse(response, null);
      }
      setConnectionDialog(null);
      if (savedConnection?.Id) {
        setSelectedConnectionId(savedConnection.Id);
      }
      await loadConnections();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteConnection = async (connectionId) => {
    if (!window.confirm('Delete this integration connection and its actions?')) return;
    setSaving(true);
    setError('');
    try {
      await apiConfig.deleteIntegrationConnection(connectionId);
      setSelectedConnectionId('');
      setActions([]);
      await loadConnections();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAction = async (form) => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        IntegrationConnectionId: selectedConnection.Id,
        Headers: parseJson(form.Headers, 'Headers'),
        QueryParams: parseJson(form.QueryParams, 'Query params'),
        BodyTemplate: parseJson(form.BodyTemplate, 'Body template'),
        InputSchema: parseJson(form.InputSchema, 'Input schema'),
        OutputSchema: parseJson(form.OutputSchema, 'Output schema'),
        TimeoutSeconds: Number(form.TimeoutSeconds || 30),
        RetryCount: Number(form.RetryCount || 0),
        UpdatedBy: userName,
      };
      if (form.Id) {
        await apiConfig.updateIntegrationAction(form.Id, body);
      } else {
        await apiConfig.createIntegrationAction({ ...body, CreatedBy: userName });
      }
      setActionDialog(null);
      await loadActions(selectedConnection.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this integration action?')) return;
    setSaving(true);
    setError('');
    try {
      await apiConfig.deleteIntegrationAction(actionId);
      await loadActions(selectedConnection.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const connectionColumns = [
    { field: 'Name', headerName: 'Connection', flex: 0.9 },
    { field: 'IntegrationType', headerName: 'Type', flex: 0.55 },
    { field: 'AuthMethod', headerName: 'Auth', flex: 0.45 },
    {
      field: 'Active',
      headerName: 'Active',
      flex: 0.35,
      renderCell: (params) => <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.55,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit connection">
            <IconButton size="small" onClick={() => setConnectionDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete connection">
            <IconButton size="small" onClick={() => deleteConnection(params.row.Id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const actionColumns = [
    { field: 'Name', headerName: 'Action', flex: 0.8 },
    { field: 'ActionType', headerName: 'Type', flex: 0.55 },
    { field: 'Method', headerName: 'Method', flex: 0.35 },
    { field: 'Path', headerName: 'Path', flex: 0.75 },
    { field: 'Topic', headerName: 'Topic', flex: 0.55 },
    {
      field: 'Active',
      headerName: 'Active',
      flex: 0.35,
      renderCell: (params) => <Chip size="small" label={params.value ? 'Yes' : 'No'} />,
    },
    {
      field: 'actionButtons',
      headerName: '',
      sortable: false,
      flex: 0.45,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit action">
            <IconButton size="small" onClick={() => setActionDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete action">
            <IconButton size="small" onClick={() => deleteAction(params.row.Id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box p={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h4">Integration Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage reusable platform connections and actions that payload workflows can trigger.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSelected}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setConnectionDialog(connectionDefaults(userName))}>
            Create Connection
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <TextField
              size="small"
              placeholder="Search integrations"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ height: 520 }}>
              <DataGrid
                rows={filteredConnections}
                columns={connectionColumns}
                getRowId={(row) => row.Id}
                loading={loading}
                pageSizeOptions={[10]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                onRowClick={(params) => setSelectedConnectionId(params.row.Id)}
                rowSelectionModel={selectedConnectionId ? [selectedConnectionId] : []}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Paper elevation={3} sx={{ p: 2 }}>
              {selectedConnection ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="h5">{selectedConnection.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConnection.BaseUrl || selectedConnection.Host || 'No endpoint configured'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={selectedConnection.IntegrationType} color="primary" />
                      <Chip label={selectedConnection.AuthMethod} />
                      <Chip label={selectedConnection.Active ? 'Active' : 'Inactive'} color={selectedConnection.Active ? 'success' : 'default'} />
                    </Stack>
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Connection ID</Typography>
                      <Typography sx={{ wordBreak: 'break-all' }}>{selectedConnection.Id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Host / Port</Typography>
                      <Typography>{selectedConnection.Host || '-'}{selectedConnection.Port ? `:${selectedConnection.Port}` : ''}</Typography>
                    </Grid>
                  </Grid>
                </Stack>
              ) : (
                <Typography color="text.secondary">Select or create an integration connection.</Typography>
              )}
            </Paper>

            {selectedConnection && (
              <Paper elevation={3} sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={2}>
                  <Box>
                    <Typography variant="h6">Integration Actions</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reusable actions under this connection. Payload workflow steps can trigger these by selecting integration_action.
                    </Typography>
                  </Box>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setActionDialog(actionDefaults(selectedConnection.Id, userName))}>
                    Add Action
                  </Button>
                </Stack>
                <Box sx={{ height: 460 }}>
                  <DataGrid
                    rows={actions}
                    columns={actionColumns}
                    getRowId={(row) => row.Id}
                    pageSizeOptions={[10]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {saving && (
        <Box sx={{ position: 'fixed', right: 24, bottom: 24, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Saving</Typography>
        </Box>
      )}

      {connectionDialog && (
        <ConnectionDialog
          open
          connection={connectionDialog}
          onClose={() => setConnectionDialog(null)}
          onSave={saveConnection}
          saving={saving}
        />
      )}

      {actionDialog && selectedConnection && (
        <ActionDialog
          open
          action={actionDialog}
          onClose={() => setActionDialog(null)}
          onSave={saveAction}
          saving={saving}
        />
      )}
    </Box>
  );
}

function ConnectionDialog({ open, connection, onClose, onSave, saving }) {
  const initial = {
    ...connection,
    Password: '',
    ApiKey: '',
    Token: '',
    Port: connection.Port || '',
    DefaultHeaders: typeof connection.DefaultHeaders === 'string' ? connection.DefaultHeaders : stringifyJson(connection.DefaultHeaders),
    Config: typeof connection.Config === 'string' ? connection.Config : stringifyJson(connection.Config),
  };
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form.Id ? 'Edit Integration Connection' : 'Create Integration Connection'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Name" value={form.Name || ''} onChange={(event) => update('Name', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Integration Type</InputLabel>
              <Select label="Integration Type" value={form.IntegrationType} onChange={(event) => update('IntegrationType', event.target.value)}>
                {INTEGRATION_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Auth Method</InputLabel>
              <Select label="Auth Method" value={form.AuthMethod} onChange={(event) => update('AuthMethod', event.target.value)}>
                {AUTH_METHODS.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Base URL" value={form.BaseUrl || ''} onChange={(event) => update('BaseUrl', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Host" value={form.Host || ''} onChange={(event) => update('Host', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Port" value={form.Port} onChange={(event) => update('Port', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Username" value={form.Username || ''} onChange={(event) => update('Username', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="Password" value={form.Password || ''} onChange={(event) => update('Password', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="API Key" value={form.ApiKey || ''} onChange={(event) => update('ApiKey', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="Token" value={form.Token || ''} onChange={(event) => update('Token', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Header Name" value={form.HeaderName || ''} onChange={(event) => update('HeaderName', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Header Prefix" value={form.HeaderPrefix || ''} onChange={(event) => update('HeaderPrefix', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={8} label="Default Headers" value={form.DefaultHeaders} onChange={(event) => update('DefaultHeaders', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={8} label="Config" value={form.Config} onChange={(event) => update('Config', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.Name} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function ActionDialog({ open, action, onClose, onSave, saving }) {
  const initial = {
    ...action,
    Headers: typeof action.Headers === 'string' ? action.Headers : stringifyJson(action.Headers),
    QueryParams: typeof action.QueryParams === 'string' ? action.QueryParams : stringifyJson(action.QueryParams),
    BodyTemplate: typeof action.BodyTemplate === 'string' ? action.BodyTemplate : stringifyJson(action.BodyTemplate),
    InputSchema: typeof action.InputSchema === 'string' ? action.InputSchema : stringifyJson(action.InputSchema),
    OutputSchema: typeof action.OutputSchema === 'string' ? action.OutputSchema : stringifyJson(action.OutputSchema),
  };
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form.Id ? 'Edit Integration Action' : 'Create Integration Action'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Name" value={form.Name || ''} onChange={(event) => update('Name', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select label="Action Type" value={form.ActionType} onChange={(event) => update('ActionType', event.target.value)}>
                {ACTION_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select label="Method" value={form.Method} onChange={(event) => update('Method', event.target.value)}>
                {HTTP_METHODS.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Path" value={form.Path || ''} onChange={(event) => update('Path', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Topic" value={form.Topic || ''} onChange={(event) => update('Topic', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Recipient" value={form.Recipient || ''} onChange={(event) => update('Recipient', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Timeout" value={form.TimeoutSeconds} onChange={(event) => update('TimeoutSeconds', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Retries" value={form.RetryCount} onChange={(event) => update('RetryCount', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={!!form.ContinueOnError} onChange={(event) => update('ContinueOnError', event.target.checked)} />} label="Continue On Error" />
              <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Headers" helperText="Set Content-Type to multipart/form-data to send this HTTP action as multipart. The boundary is generated automatically." value={form.Headers} onChange={(event) => update('Headers', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Query Params" value={form.QueryParams} onChange={(event) => update('QueryParams', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Body Template" helperText={'Multipart file: {"$file":{"filename":"file.jpg","content":"{{input.file_base64}}","encoding":"base64","content_type":"image/jpeg"}}'} value={form.BodyTemplate} onChange={(event) => update('BodyTemplate', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={8} label="Input Schema" value={form.InputSchema} onChange={(event) => update('InputSchema', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={8} label="Output Schema" value={form.OutputSchema} onChange={(event) => update('OutputSchema', event.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.Name} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default IntegrationManagement;
